import React, { useState, useEffect, useMemo } from "react";
import { FaSearch, FaShoppingCart, FaSpinner } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, incrementQty, decrementQty } from "../redux/cartSlice";
import { getProductImage } from "../utils/getProductImage";
import { API_BASE_URL } from "../config/apiConfig";
import Navbar from "../components/Navbar";
import HlcFooter from "../components/HlcFooter";
import { Link } from "react-router-dom";

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("default");
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart.items);

  // Fetch products from adminhlc-api (MySQL) — Step 5 of the Firebase->PHP migration
  // ROLLBACK: original Firebase implementation:
  // useEffect(() => {
  //   const productsRef = dbRef(db, "products");
  //   const unsubscribe = onValue(productsRef, (snapshot) => {
  //     const data = snapshot.val();
  //     if (data) {
  //       const productList = Object.keys(data).map((key) => ({
  //         id: key,
  //         ...data[key],
  //       }));
  //       setProducts(productList);
  //     } else {
  //       setProducts([]);
  //     }
  //     setLoading(false);
  //   });
  //   return () => unsubscribe();
  // }, []);
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/products/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch products:", err);
        if (!cancelled) {
          setProducts([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Unique categories
  const categories = useMemo(() => {
    const cats = products.map((p) => p.category || "Uncategorized");
    return ["All", ...new Set(cats)];
  }, [products]);

  // Filter + Sort
  const filteredProducts = useMemo(() => {
    let filtered = products.filter((p) => {
      const matchCategory = category === "All" || p.category === category;
      const matchSearch =
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.desc?.toLowerCase().includes(search.toLowerCase());
      return matchCategory && matchSearch;
    });

    switch (sort) {
      case "priceLow":
        return filtered.sort(
          (a, b) => parseFloat(a.discountedPrice || a.price) - parseFloat(b.discountedPrice || b.price)
        );
      case "priceHigh":
        return filtered.sort(
          (a, b) => parseFloat(b.discountedPrice || b.price) - parseFloat(a.discountedPrice || a.price)
        );
      case "name":
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return filtered;
    }
  }, [category, search, sort, products]);

  const getCartItem = (id) => cart.find((item) => item.id === id);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <FaSpinner className="animate-spin text-4xl text-[#9b1f6c]" />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <section className="w-full bg-gray-50 pt-28 pb-12 px-6 md:px-12 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="sr-only">All Products</h1>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10 bg-white border border-[#eceaf1] rounded-lg p-4 shadow-sm">
          <div className="relative w-full sm:w-1/3">
            <FaSearch className="absolute top-3 left-3 text-gray-400" />
            <input
              type="text"
              aria-label="Search products"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2 border border-[#eceaf1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#9b1f6c] text-[#35335b]"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={category}
              aria-label="Filter by category"
              onChange={(e) => setCategory(e.target.value)}
              className="border border-[#eceaf1] px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9b1f6c] text-[#35335b]"
            >
              {categories.map((cat) => (
                <option key={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={sort}
              aria-label="Sort products"
              onChange={(e) => setSort(e.target.value)}
              className="border border-[#eceaf1] px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9b1f6c] text-[#35335b]"
            >
              <option value="default">Sort By</option>
              <option value="priceLow">Price: Low to High</option>
              <option value="priceHigh">Price: High to Low</option>
              <option value="name">Name: A-Z</option>
            </select>
          </div>
        </div>

        {/* Product Grid */}
        {filteredProducts.length === 0 ? (
          <p className="text-center text-gray-500 mt-20 text-lg">
            No products found.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {filteredProducts.map((p, idx) => {
              const cartItem = getCartItem(p.id);
              const price = parseFloat(p.discountedPrice || p.price);
              const oldPrice = p.discount ? p.price : null;

              return (
                <div
                  key={p.id}
                  className="bg-white rounded-2xl shadow hover:shadow-lg transition overflow-hidden flex flex-col min-w-0"
                >
                  <div className="relative h-44 bg-gray-100 overflow-hidden">
                    <img
                      src={getProductImage(p)}
                      alt={p.name}
                      loading={idx < 8 ? "eager" : "lazy"}
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    {p.discount && (
                      <span className="absolute top-2 left-2 bg-[#7b1fa2] text-white text-xs font-medium px-2 py-1 rounded">
                        {p.discount}
                      </span>
                    )}
                  </div>

                  <div className="p-4 flex flex-col justify-between min-h-36 flex-1">
                    <div>
                      <h2 className="text-sm md:text-base font-semibold text-gray-900">
                        <Link to={`/product/${p.id}`}>{p.name}</Link>
                      </h2>
                      <p className="text-xs text-gray-500 mt-1">{p.category}</p>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2 items-end justify-between">
                      <div>
                        <div className="text-sm font-bold text-gray-900">
                          ₨ {price.toLocaleString()}
                        </div>
                        {oldPrice && (
                          <div className="text-xs text-gray-400 line-through">
                            ₨ {parseFloat(oldPrice).toLocaleString()}
                          </div>
                        )}
                      </div>

                      {/* Add Button or Quantity Counter */}
                      {!cartItem ? (
                        <button
                          onClick={() => dispatch(addToCart(p))}
                          className="bg-[#7b1fa2] hover:bg-[#6e1a8f] text-white px-3 py-2 rounded-full text-xs font-semibold flex items-center gap-1 shadow"
                        >
                          <FaShoppingCart size={12} /> Add
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 bg-[#7b1fa2] text-white px-2 py-1 rounded-full">
                          <button
                            onClick={() => dispatch(decrementQty(p.id))}
                            className="px-2 text-lg font-bold"
                          >
                            −
                          </button>
                          <span className="text-sm font-semibold">
                            {cartItem.quantity}
                          </span>
                          <button
                            onClick={() => dispatch(incrementQty(p.id))}
                            className="px-2 text-lg font-bold"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </section>
      <HlcFooter />
    </>
  );
};

export default AllProducts;
